import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import Lightbox from '@/components/Lightbox';
import MaskEditor from '@/components/MaskEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FILTERS = [
  { key: 'pending', label: 'À revoir', q: '?review_status=pending&gen_status=completed' },
  { key: 'processing', label: 'En cours', q: '?gen_status=queued,processing' },
  { key: 'approved', label: 'Validés', q: '?review_status=approved' },
  { key: 'failed', label: 'Échecs', q: '?gen_status=failed' },
  { key: 'rejected', label: 'Rejetés', q: '?include_rejected=1&review_status=rejected' },
];
const FILTER_KEYS = FILTERS.map((f) => f.key);

const StudioPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get('filter');
  const filter = FILTER_KEYS.includes(filterParam) ? filterParam : 'pending';
  const setFilter = (next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('filter', next);
      return p;
    }, { replace: true });
  };
  const [assets, setAssets] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('portrait_16_9');
  const [busy, setBusy] = useState(false);
  const [refAsset, setRefAsset] = useState(null);
  const [refScale, setRefScale] = useState(0.7);
  const [showRefPicker, setShowRefPicker] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [retouching, setRetouching] = useState(null);
  const pollRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAssets = useCallback(async (f) => {
    const conf = FILTERS.find((x) => x.key === f) || FILTERS[0];
    try {
      const r = await axios.get(`${API}/admin/assets${conf.q}`, { headers: authHeaders(), withCredentials: true });
      setAssets(r.data);
    } catch (e) { /* silencieux */ }
  }, []);

  useEffect(() => { fetchAssets(filter); }, [filter, fetchAssets]);

  // Polling batch tant qu'il reste des générations en cours
  useEffect(() => {
    const tick = async () => {
      const r = await axios.get(`${API}/admin/assets?gen_status=queued`, { headers: authHeaders(), withCredentials: true }).catch(() => null);
      const r2 = await axios.get(`${API}/admin/assets?gen_status=processing`, { headers: authHeaders(), withCredentials: true }).catch(() => null);
      const inflight = (r?.data?.length || 0) + (r2?.data?.length || 0);
      if (inflight > 0) fetchAssets(filter);
    };
    pollRef.current = setInterval(tick, 4000);
    return () => clearInterval(pollRef.current);
  }, [filter, fetchAssets]);

  const openRefPicker = async () => {
    if (mediaFiles.length === 0) {
      try {
        const r = await axios.get(`${API}/admin/media`, { headers: authHeaders(), withCredentials: true });
        setMediaFiles(r.data.files || []);
      } catch (e) { toast.error('Médiathèque indisponible'); }
    }
    setShowRefPicker(true);
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.warning('Écris un prompt'); return; }
    setBusy(true);
    try {
      const payload = { prompt, image_size: size };
      if (refAsset) { payload.reference_asset_id = refAsset.id; payload.reference_scale = refScale; }
      await axios.post(`${API}/admin/generate-image`, payload,
        { headers: authHeaders(), withCredentials: true });
      toast.success('Génération lancée — apparaît dans « En cours »');
      setPrompt('');
      setFilter('processing');
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec du lancement'); }
    finally { setBusy(false); }
  };

  const review = async (id, decision) => {
    try {
      await axios.post(`${API}/admin/assets/${id}/review`, { decision }, { headers: authHeaders(), withCredentials: true });
      toast.success(decision === 'approved' ? 'Validé' : 'Rejeté');
      fetchAssets(filter);
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec de la validation'); }
  };
  const variant = async (id) => {
    try {
      await axios.post(`${API}/admin/assets/${id}/variant`, {}, { headers: authHeaders(), withCredentials: true });
      toast.success('Variante lancée');
      setFilter('processing');
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec de la variante'); }
  };

  // La retouche crée un asset ENFANT en attente de revue : l'original n'est
  // jamais modifié. On relance l'erreur pour que l'éditeur reste ouvert et
  // que les traits déjà peints ne soient pas perdus.
  const submitRetouch = async ({ maskPng, prompt: zonePrompt, strength }) => {
    const target = retouching;
    try {
      await axios.post(`${API}/admin/assets/${target.id}/retouch`,
        { prompt: zonePrompt, mask_png: maskPng, strength },
        { headers: authHeaders(), withCredentials: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la retouche');
      throw e;
    }
    toast.success('Retouche lancée — apparaît dans « En cours »');
    setRetouching(null);
    setFilter('processing');
    fetchAssets('processing');
  };

  return (
    <div className="studio-panel" data-testid="studio-panel">
      <Lightbox src={lightbox?.src || ''} alt={lightbox?.alt} onClose={() => setLightbox(null)} />

      {retouching && (
        <MaskEditor
          src={`${BACKEND}${retouching.media_url}`}
          onCancel={() => setRetouching(null)}
          onSubmit={submitRetouch}
        />
      )}

      <div className="form-section" style={{ marginBottom: '1.5rem' }}>
        <label className="form-label form-section-title">Générer un visuel (LoRA Elara)</label>
        <textarea className="form-textarea" style={{ minHeight: 80 }} value={prompt}
          onChange={(e) => setPrompt(e.target.value)} placeholder="Prompt en anglais, style photojournalistique…"
          data-testid="studio-prompt" />
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <select className="form-input" style={{ maxWidth: 220 }} value={size} onChange={(e) => setSize(e.target.value)}>
            <option value="portrait_16_9">Portrait 9:16</option>
            <option value="square_hd">Carré HD</option>
            <option value="landscape_16_9">Paysage 16:9</option>
          </select>
          <button className="btn btn-primary" onClick={generate} disabled={busy} data-testid="studio-generate">
            {busy ? 'Lancement…' : 'Générer'}
          </button>
        </div>
        <div className="ref-row">
          {refAsset ? (
            <div className="ref-chip">
              <button
                type="button"
                className="img-button"
                onClick={() => setLightbox({ src: `${BACKEND}${refAsset.url}`, alt: 'référence' })}
                aria-label="Agrandir l'image de référence"
              >
                <img src={`${BACKEND}${refAsset.url}`} alt="référence" />
              </button>
              <span>réf. de lieu</span>
              <button type="button" onClick={() => setRefAsset(null)}>✕</button>
            </div>
          ) : (
            <button type="button" className="btn btn-outline" onClick={openRefPicker} data-testid="add-ref">+ image de référence</button>
          )}
          {refAsset && (
            <label className="ref-scale">influence
              <input type="range" min="0.3" max="1" step="0.05" value={refScale}
                onChange={(e) => setRefScale(parseFloat(e.target.value))} />
              <span>{refScale.toFixed(2)}</span>
            </label>
          )}
        </div>
      </div>

      {showRefPicker && (
        <div className="modal-overlay" onClick={() => setShowRefPicker(false)}>
          <div className="modal-content media-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="media-picker-head">
              <h2 className="modal-title" style={{ marginBottom: 0 }}>Image de référence</h2>
              <button type="button" className="btn btn-outline" onClick={() => setShowRefPicker(false)}>Fermer</button>
            </div>
            <div className="media-grid">
              {mediaFiles.map((f) => (
                // Div + role="button" (pas <button>) : la loupe ci-dessous est un
                // vrai bouton imbriqué, invalide en HTML dans un <button>.
                <div key={f.id} role="button" tabIndex={0} className="media-tile"
                  onClick={() => { setRefAsset(f); setShowRefPicker(false); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setRefAsset(f);
                      setShowRefPicker(false);
                    }
                  }}>
                  <img src={`${BACKEND}${f.url}`} alt={f.path} loading="lazy" />
                  <button
                    type="button"
                    className="tile-zoom"
                    aria-label="Agrandir"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox({ src: `${BACKEND}${f.url}`, alt: f.path });
                    }}
                  >
                    ⤢
                  </button>
                  {f.env_role && <span className="media-tile-role-badge">{f.env_role}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="media-picker-folders">
        {FILTERS.map((f) => (
          <button key={f.key} className={`media-folder-tab${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)} data-testid={`studio-filter-${f.key}`}>{f.label}</button>
        ))}
      </div>

      <div className="media-grid" data-testid="studio-grid">
        {assets.map((a) => (
          <div key={a.id} className="studio-tile" data-testid={`studio-asset-${a.id}`}>
            <div className="studio-thumb">
              {a.media_url
                ? (
                  <button
                    type="button"
                    className="img-button"
                    onClick={() => setLightbox({ src: `${BACKEND}${a.media_url}`, alt: a.prompt || '' })}
                    aria-label="Agrandir"
                  >
                    <img src={`${BACKEND}${a.media_url}`} alt={a.prompt || ''} loading="lazy" />
                  </button>
                )
                : <div className="studio-processing">{a.gen_status === 'failed' ? 'échec' : '⋯ en cours'}</div>}
            </div>
            <div className="studio-actions">
              {a.review_status === 'pending' && a.gen_status === 'completed' && (
                <>
                  <button className="btn btn-primary" onClick={() => review(a.id, 'approved')}>Valider</button>
                  <button className="btn btn-outline" onClick={() => review(a.id, 'rejected')}>Rejeter</button>
                  <button className="btn btn-secondary" onClick={() => variant(a.id)}>Variante</button>
                </>
              )}
              {a.review_status === 'approved' && <button className="btn btn-secondary" onClick={() => variant(a.id)}>Variante</button>}
              {a.gen_status === 'completed' && a.media_url && (a.type || 'image') === 'image' && (
                <button className="btn btn-secondary" onClick={() => setRetouching(a)}
                  data-testid={`studio-retouch-${a.id}`}>Retoucher</button>
              )}
              {a.review_status === 'rejected' && <button className="btn btn-secondary" onClick={() => review(a.id, 'approved')}>Restaurer</button>}
            </div>
          </div>
        ))}
        {assets.length === 0 && <p className="form-hint">Rien ici pour l'instant.</p>}
      </div>
    </div>
  );
};

export default StudioPanel;
